import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import type { CSSProperties, FocusEventHandler, JSX, ReactNode } from 'react';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import type { Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Markdown } from '@tiptap/markdown';
import Image from '@tiptap/extension-image';
import { TableKit } from '@tiptap/extension-table';
import { TaskItem, TaskList } from '@tiptap/extension-list';
import { Placeholder } from '@tiptap/extensions';
import { Bold, Heading2, Italic, Link2, List, ListOrdered, Quote, Redo2, Undo2 } from 'lucide-react';
import { IconButton } from './icon-button';
import InputDialog from '../InputDialog';
import { safeUrl } from '../ai/TiaMarkdown';
import { cn } from '@/lib/utils';
import { useI18n } from '../../i18n';

/**
 * The text editor of a record, a note and a beat's prep (owner 29.10: «нормальный редактор текстовый»): WYSIWYG on
 * TipTap (ProseMirror), the text is STORED as Markdown — what Tia and every reader (TiaMarkdown, react-markdown) take.
 * Kept from the old textarea: value/onValueChange, placeholder, disabled/readOnly, focus/blur for the autosave and its
 * «не перетирать поле, в котором печатают» (JournalEntryEditor), undo/redo, Ctrl+B/I, Ctrl+Enter of a form (onKeyDown
 * goes first; a prevented key is not handled by the editor). The block takes the whole width of its column.
 */

export interface MarkdownEditorHandle {
  focus: () => void;
  /** The editable element (contenteditable). */
  readonly element: HTMLElement | null;
}

export interface MarkdownEditorProps {
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  /** Classes of the field (the editable area): its height, background, text size. */
  readonly className?: string;
  /** The minimal height in lines (the old textarea `rows`). */
  readonly rows?: number;
  readonly autoFocus?: boolean;
  readonly onFocus?: FocusEventHandler<HTMLDivElement>;
  readonly onBlur?: FocusEventHandler<HTMLDivElement>;
  /** Before the editor: preventDefault() keeps the key from the editor (Ctrl+Enter = «Готово» of a form). */
  readonly onKeyDown?: (event: KeyboardEvent) => void;
  /** On the editable element (tests and the focus lookups find the field by it). */
  readonly 'data-testid'?: string;
  readonly 'aria-label'?: string;
  readonly 'aria-labelledby'?: string;
  readonly id?: string;
}

/** The node set of the editor: everything a reader of Markdown shows, so a stored text survives an edit. */
export function markdownExtensions(placeholder?: () => string) {
  return [
    StarterKit.configure({
      // Not Markdown: it would be lost on save.
      underline: false,
      link: { openOnClick: false, autolink: true, linkOnPaste: true, defaultProtocol: 'https', protocols: ['http', 'https', 'mailto'] },
    }),
    TableKit.configure({ table: { resizable: false } }),
    TaskList,
    TaskItem.configure({ nested: true }),
    // An image in a stored text: without this node its URL was dropped on save (only the alt text stayed).
    Image.configure({ inline: true }),
    Markdown,
    ...(placeholder ? [Placeholder.configure({ placeholder })] : []),
  ];
}

/**
 * The Markdown of the editor as it is stored. The empty paragraph the editor keeps after a list / a quote / a table at
 * the end (so the cursor can get below it) is not text: it is not written as trailing blank lines.
 */
export function markdownOf(editor: Pick<Editor, 'getMarkdown'>): string {
  return editor.getMarkdown().replace(/\n+$/, '');
}

const KEYS = /Mac|iPhone|iPad/.test(typeof navigator === 'undefined' ? '' : navigator.platform) ? '⌘' : 'Ctrl+';

export const MarkdownEditor = forwardRef<MarkdownEditorHandle, MarkdownEditorProps>(function MarkdownEditor(
  {
    value,
    onValueChange,
    placeholder,
    disabled = false,
    readOnly = false,
    className,
    rows,
    autoFocus,
    onFocus,
    onBlur,
    onKeyDown,
    'data-testid': testid,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    id,
  },
  ref,
) {
  const { t } = useI18n();
  const editable = !disabled && !readOnly;
  // What the editor itself last said: the same value coming back from the host is not put in again (the caret stays).
  const emitted = useRef(value);
  const onChangeRef = useRef(onValueChange);
  onChangeRef.current = onValueChange;
  const onKeyDownRef = useRef(onKeyDown);
  onKeyDownRef.current = onKeyDown;
  const placeholderRef = useRef(placeholder ?? '');
  placeholderRef.current = placeholder ?? '';
  const [askLink, setAskLink] = useState<string | null>(null);

  const editor = useEditor({
    extensions: markdownExtensions(() => placeholderRef.current),
    content: value,
    contentType: 'markdown',
    editable,
    autofocus: autoFocus === true ? 'end' : false,
    editorProps: {
      attributes: {
        class: 'md-editor__field tia-md',
        role: 'textbox',
        'aria-multiline': 'true',
        ...(testid !== undefined ? { 'data-testid': testid } : {}),
        ...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {}),
        ...(ariaLabelledby !== undefined ? { 'aria-labelledby': ariaLabelledby } : {}),
        ...(id !== undefined ? { id } : {}),
      },
      handleKeyDown: (_view, event) => {
        onKeyDownRef.current?.(event);
        return event.defaultPrevented;
      },
    },
    onUpdate: ({ editor: ed }) => {
      const next = markdownOf(ed);
      if (next === emitted.current) return;
      emitted.current = next;
      onChangeRef.current(next);
    },
  });

  // A value from outside (another editor saved, the host switched the record) replaces the text; the host decides
  // whether a field being typed in takes it (JournalEntryEditor keeps the focused field).
  useEffect(() => {
    if (editor === null || editor.isDestroyed || value === emitted.current) return;
    emitted.current = value;
    editor.commands.setContent(value, { contentType: 'markdown', emitUpdate: false });
  }, [editor, value]);

  useEffect(() => {
    if (editor !== null && !editor.isDestroyed && editor.isEditable !== editable) editor.setEditable(editable, false);
  }, [editor, editable]);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        if (editor !== null && !editor.isDestroyed) editor.commands.focus('end');
      },
      get element() {
        return editor !== null && !editor.isDestroyed ? (editor.view.dom as HTMLElement) : null;
      },
    }),
    [editor],
  );

  const style: CSSProperties | undefined = rows !== undefined ? { minHeight: `calc(${rows * 1.5}em + 16px)` } : undefined;

  return (
    <div
      className="md-editor flex w-full min-w-0 flex-1 flex-col overflow-hidden rounded-md border border-input bg-background focus-within:border-hl/50"
      data-testid="markdown-editor"
      data-disabled={!editable || undefined}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      <Toolbar editor={editor} disabled={!editable} onLink={() => setAskLink(editor?.getAttributes('link').href ?? '')} />
      <EditorContent editor={editor} className={cn('md-editor__body flex min-h-0 flex-1 flex-col', className)} style={style} />
      {askLink !== null ? (
        <InputDialog
          open
          onOpenChange={(open) => {
            if (!open) setAskLink(null);
          }}
          title={t('editor.linkTitle')}
          label={t('editor.linkLabel')}
          placeholder="https://"
          defaultValue={askLink}
          maxLength={2000}
          validate={(url) => (url.trim() === '' || safeUrl(url) !== '' ? null : t('editor.linkInvalid'))}
          onConfirm={(url) => {
            setAskLink(null);
            if (editor === null) return;
            const chain = editor.chain().focus().extendMarkRange('link');
            if (url.trim() === '') chain.unsetLink().run();
            else chain.setLink({ href: url.trim() }).run();
          }}
          onCancel={() => {
            setAskLink(null);
            editor?.commands.focus();
          }}
        />
      ) : null}
    </div>
  );
});

function Toolbar({ editor, disabled, onLink }: { readonly editor: Editor | null; readonly disabled: boolean; readonly onLink: () => void }): JSX.Element {
  const { t } = useI18n();
  const state = useEditorState({
    editor,
    selector: ({ editor: ed }) =>
      ed === null
        ? null
        : {
            bold: ed.isActive('bold'),
            italic: ed.isActive('italic'),
            heading: ed.isActive('heading'),
            bullet: ed.isActive('bulletList'),
            ordered: ed.isActive('orderedList'),
            quote: ed.isActive('blockquote'),
            link: ed.isActive('link'),
            undo: ed.can().undo(),
            redo: ed.can().redo(),
          },
  });
  const run = (fn: (ed: Editor) => void) => (): void => {
    if (editor !== null && !disabled) fn(editor);
  };
  const button = (label: string, icon: ReactNode, onClick: () => void, pressed?: boolean, off?: boolean, keys?: string): JSX.Element => (
    <IconButton
      key={label}
      size="sm"
      label={label}
      hint={keys !== undefined ? `${label} (${keys})` : label}
      icon={icon}
      aria-pressed={pressed}
      disabled={disabled || off === true}
      className={pressed === true ? 'bg-hl/13 text-hl' : undefined}
      // The selection stays in the text while a button is pressed.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    />
  );
  return (
    <div className="flex flex-none flex-wrap items-center gap-0.5 border-b border-line px-1 py-[3px]" role="toolbar" aria-label={t('editor.format')} data-testid="markdown-editor-toolbar">
      {button(t('editor.bold'), <Bold size={14} aria-hidden="true" />, run((ed) => ed.chain().focus().toggleBold().run()), state?.bold, false, `${KEYS}B`)}
      {button(t('editor.italic'), <Italic size={14} aria-hidden="true" />, run((ed) => ed.chain().focus().toggleItalic().run()), state?.italic, false, `${KEYS}I`)}
      {button(t('editor.heading'), <Heading2 size={14} aria-hidden="true" />, run((ed) => ed.chain().focus().toggleHeading({ level: 2 }).run()), state?.heading)}
      {button(t('editor.list'), <List size={14} aria-hidden="true" />, run((ed) => ed.chain().focus().toggleBulletList().run()), state?.bullet)}
      {button(t('editor.orderedList'), <ListOrdered size={14} aria-hidden="true" />, run((ed) => ed.chain().focus().toggleOrderedList().run()), state?.ordered)}
      {button(t('editor.quote'), <Quote size={14} aria-hidden="true" />, run((ed) => ed.chain().focus().toggleBlockquote().run()), state?.quote)}
      {button(t('editor.link'), <Link2 size={14} aria-hidden="true" />, run(() => onLink()), state?.link)}
      <span className="mx-0.5 h-4 w-px bg-line" aria-hidden="true" />
      {button(t('editor.undo'), <Undo2 size={14} aria-hidden="true" />, run((ed) => ed.chain().focus().undo().run()), undefined, state?.undo !== true, `${KEYS}Z`)}
      {button(t('editor.redo'), <Redo2 size={14} aria-hidden="true" />, run((ed) => ed.chain().focus().redo().run()), undefined, state?.redo !== true, `${KEYS}Y`)}
    </div>
  );
}
